import React, { useEffect, useState, useRef, useMemo, useId } from 'react';
import apiClient from '../config/api';
import enhancedModalStyles from '../styles/enhancedModalStyles';

const RENDERED_ELIGIBLE_TYPES = ['Rendition'];

const getOwnerKey = (ownerNbr, relationTypeId) => `${ownerNbr}_${relationTypeId}`;

const parseBooleanValue = (value) => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value === 1;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (['1', 'true', 'yes', 'y'].includes(normalized)) return true;
    if (['0', 'false', 'no', 'n'].includes(normalized)) return false;
  }
  return false;
};

const normalizeDateValue = (value) => {
  if (!value) return '';
  if (typeof value === 'string') return value.split('T')[0];
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? '' : parsed.toISOString().slice(0, 10);
};

const toNumberOrBlank = (value) => {
  if (value === '' || value === null || value === undefined) return '';
  const parsed = Number(value);
  return Number.isNaN(parsed) ? '' : parsed;
};

const StatusModal = ({
  statusModalFile,
  newStatusId,
  setNewStatusId,
  statusOptions,
  setStatusModalFile,
  handleStatusUpdate,
  isSavingStatus,
  isAdmin,
  styles,
  // modalStyles
}) => {
  const [countyName, setCountyName] = useState('');
  const [orgOptions, setOrgOptions] = useState([]);
  const [relationTypes, setRelationTypes] = useState([]);
  const [owners, setOwners] = useState([]);
  const [searchPartyNumber, setSearchPartyNumber] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [suggestedOrgs, setSuggestedOrgs] = useState([]);
  const [matchedOrg, setMatchedOrg] = useState(null);
  const [selectedRelationTypeId, setSelectedRelationTypeId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [description, setDescription] = useState('');
  const [assignedOwners, setAssignedOwners] = useState([]);
  const [unlinkingOwnerId, setUnlinkingOwnerId] = useState(null); // partyNumber used for identifying which one is in confirmation mode
  const [isUnlinking, setIsUnlinking] = useState(false); // to show loader or disable confirm button
  // Agent unlink confirm state
  const [unlinkingAgentKey, setUnlinkingAgentKey] = useState(null);
  const [isUnlinkingAgent, setIsUnlinkingAgent] = useState(false);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState('');
  const [availableTypes, setAvailableTypes] = useState([]);
  const [newTypeId, setNewTypeId] = useState('');
  const [typeError, setTypeError] = useState('');
  const [originalCategoryId, setOriginalCategoryId] = useState('');
  const [originalTypeId, setOriginalTypeId] = useState('');
  const [ownerRenderedStates, setOwnerRenderedStates] = useState({});
  const baseModalStyles = enhancedModalStyles;
  // Agent assignment state
  const [agentTerm, setAgentTerm] = useState('');
  const [agentDebouncedTerm, setAgentDebouncedTerm] = useState('');
  const [agentResults, setAgentResults] = useState([]);
  const [selectedAgents, setSelectedAgents] = useState([]); // [{ AgentName, LegacyCode }]
  const [agentRelationTypeId, setAgentRelationTypeId] = useState('');

  const [assignedAgents, setAssignedAgents] = useState([]);
  const [isCompactLayout, setIsCompactLayout] = useState(false);

  const statusSelectId = useId();
  const categorySelectId = useId();
  const attachmentTypeSelectId = useId();
  const descriptionTextareaId = useId();
  const ownerRelationTypeSelectId = useId();
  const partyNumberInputId = useId();
  const agentRelationTypeSelectId = useId();
  const agentSearchInputId = useId();

  // Refs to prevent duplicate calls in StrictMode
  const saveInProgressRef = useRef(false);
  const categoryDraftTypeRef = useRef({});
  const renderedOwnersRef = useRef({});
  const contentAreaRef = useRef(null);

  const scrollModalToTop = (behavior = 'smooth') => {
    const contentArea = contentAreaRef.current;
    if (!contentArea) return;

    const performScroll = () => {
      if (typeof contentArea.scrollTo === 'function') {
        contentArea.scrollTo({ top: 0, behavior });
      } else {
        contentArea.scrollTop = 0;
      }
    };

    if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(performScroll);
    } else {
      performScroll();
    }
  };

  const showModalError = (message) => {
    setError(message);
    scrollModalToTop();
  };

  // Reset modal state when modal opens/closes
  useEffect(() => {
    if (statusModalFile) {
      // Reset all state when modal opens
      setCountyName('');
      setOrgOptions([]);
      setRelationTypes([]);
      setOwners([]);
      setAssignedOwners([]);
      setAssignedAgents([]);
      setSearchPartyNumber('');
      setDebouncedSearch('');
      setSuggestedOrgs([]);
      setMatchedOrg(null);
      setSelectedRelationTypeId('');
      setError('');
      setIsSaving(false);
      setCategoryOptions([]);
      setSelectedCategoryId('');
      setAvailableTypes([]);
      setNewTypeId('');
      setTypeError('');
      setOriginalCategoryId('');
      setOriginalTypeId('');
      setDescription('');
      setOwnerRenderedStates({});
      setAgentTerm('');
      setAgentDebouncedTerm('');
      setAgentResults([]);
      setSelectedAgents([]);
      setAgentRelationTypeId('');
      setUnlinkingOwnerId(null);
      setUnlinkingAgentKey(null);
      // Reset refs
      saveInProgressRef.current = false;
      categoryDraftTypeRef.current = {};
      renderedOwnersRef.current = {};
    }
  }, [statusModalFile]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const evaluateLayout = () => setIsCompactLayout(window.innerWidth <= 640);
    evaluateLayout();
    window.addEventListener('resize', evaluateLayout);
    return () => window.removeEventListener('resize', evaluateLayout);
  }, []);

  const modalStyles = useMemo(() => {
    if (!isCompactLayout) return baseModalStyles;
    return {
      ...baseModalStyles,
      overlay: {
        ...baseModalStyles.overlay,
        padding: '12px'
      },
      box: {
        ...baseModalStyles.box,
        borderRadius: '18px',
        maxHeight: '94vh'
      },
      contentArea: {
        ...baseModalStyles.contentArea,
        paddingBottom: '8px'
      },
      contentContainer: {
        ...baseModalStyles.contentContainer,
        padding: '20px 14px',
        gap: '20px',
        alignItems: 'stretch'
      },
      fileInfoSection: {
        ...baseModalStyles.fileInfoSection,
        width: '92%',
        maxWidth: '340px',
        margin: '0 auto',
        padding: '16px 12px',
        gap: '12px',
        gridTemplateColumns: '1fr'
      },
      fileInfoItem: {
        ...baseModalStyles.fileInfoItem,
        padding: '12px 12px',
        borderRadius: '14px'
      },
      fileInfoLabel: {
        ...baseModalStyles.fileInfoLabel,
        fontSize: '13px'
      },
      fileInfoValue: {
        ...baseModalStyles.fileInfoValue,
        width: '86%',
        padding: '6px 10px',
        fontSize: '15px'
      },
      formSectionsContainer: {
        ...baseModalStyles.formSectionsContainer,
        gap: '14px',
        gridTemplateColumns: '1fr'
      },
      formSection: {
        ...baseModalStyles.formSection,
        padding: '12px 14px',
        borderRadius: '16px'
      },
      fullWidthSection: {
        ...baseModalStyles.fullWidthSection,
        padding: '14px 16px',
        borderRadius: '18px'
      },
      modalInput: {
        ...baseModalStyles.modalInput,
        padding: '12px 14px'
      },
      modalSelect: {
        ...baseModalStyles.modalSelect,
        padding: '12px 14px'
      },
      modalTextarea: {
        ...baseModalStyles.modalTextarea,
        padding: '12px 14px'
      },
      ownerFieldsContainer: {
        ...baseModalStyles.ownerFieldsContainer,
        gap: '14px'
      },
      ownerCard: {
        ...baseModalStyles.ownerCard,
        padding: '14px 16px'
      },
      saveButton: {
        ...baseModalStyles.saveButton,
        maxWidth: '260px',
        padding: '14px 24px',
        marginTop: '24px',
        marginBottom: '24px'
      },
      loadingMessage: {
        ...baseModalStyles.loadingMessage,
        padding: '12px 14px'
      }
    };
  }, [baseModalStyles, isCompactLayout]);

  useEffect(() => {
    if (!statusModalFile) return;

    setLoading(true);

    apiClient.get(`/status/uploads/${statusModalFile.FileID}/metadata`, { withCredentials: true })
      .then(res => {
        const nextCountyName = res.data.CountyName;
        setCountyName(nextCountyName);

        if (!isAdmin) {
          setLoading(false);
          return null;
        }

        return apiClient.get(`/lookup/OrganizationName?county=${encodeURIComponent(nextCountyName)}`, { withCredentials: true });
      })
      .then(res => {
        if (!isAdmin || !res) return;
        setOrgOptions(res.data || []);
        setLoading(false);
      })
      .catch(err => {
        console.error('Error loading organization data:', err);
        setError(`Failed to load organization data: ${err.response?.data?.error || err.message}`);
        setLoading(false);
      });

    // Step 1.5: Fetch Description ,type and render info 

    // apiClient.get(`http://www.typortal.com/api/uploads/${statusModalFile.FileID}/descriptiontypes`, { withCredentials: true })
    // .then(async res => {
    //   const { attachmentCategoryId, attachmentTypeId, description } = res.data;
    //   setDescription(description || '');
    //   setNewTypeId(attachmentTypeId || '');

    //   // Now fetch types for that category
    //   try {
    //     const typeRes = await apiClient.get(`http://www.typortal.com/api/types/${attachmentCategoryId}`, {
    //       withCredentials: true
    //     });
    //     setAvailableTypes(typeRes.data || []);
    //   } catch (typeErr) {
    //     console.error('Failed to fetch types:', typeErr);
    //     setAvailableTypes([]);
    //   }
    // })

    apiClient.get(`/status/uploads/${statusModalFile.FileID}/descriptiontypes`, {
      withCredentials: true
    })
    .then(async res => {
      const { attachmentCategoryId, attachmentTypeId, description } = res.data;
      const normalizedCategoryId = toNumberOrBlank(attachmentCategoryId);
      const normalizedTypeId = toNumberOrBlank(attachmentTypeId);

      setDescription(description || '');
      setSelectedCategoryId(normalizedCategoryId);
      setNewTypeId(normalizedTypeId);
      setOriginalCategoryId(normalizedCategoryId);
      setOriginalTypeId(normalizedTypeId);
      setTypeError('');
      categoryDraftTypeRef.current = normalizedCategoryId
        ? { [String(normalizedCategoryId)]: normalizedTypeId || '' }
        : {};

      try {
        // Load categories and types for the current saved category.
        const [categoriesRes, typeRes] = await Promise.all([
          apiClient.get('/lookup/categories', { withCredentials: true }),
          normalizedCategoryId
            ? apiClient.get(`/lookup/types/${normalizedCategoryId}`, { withCredentials: true })
            : Promise.resolve({ data: [] })
        ]);

        setCategoryOptions(categoriesRes.data || []);
        setAvailableTypes(typeRes.data || []);

        // Detect industrial rendition — rendered section loads dynamically once owner is assigned
        // Draft rendered state is derived later from the live category/type selection.

      } catch (typeErr) {
        console.error('Failed to fetch types:', typeErr);
        setCategoryOptions([]);
        setAvailableTypes([]);
      }
    })
    .catch(err => {
      console.error('Failed to fetch file description/types:', err);
    });



    if (isAdmin) {
      // Step 3: Fetch relation types (separate request to avoid blocking)
      apiClient.get('/lookup/relation-types', { withCredentials: true })
        .then(res => {
          setRelationTypes(res.data || []);
        })
        .catch(err => {
          console.error('Error loading relation types:', err);
        });

      apiClient.get('/status/uploads/' + statusModalFile.FileID + '/owners', {
          withCredentials: true
        })
        .then(res => {
          setAssignedOwners(res.data || []);
        })
        .catch(err => {
          console.error('Failed to fetch assigned owners:', err);
          setAssignedOwners([]);
        });

      apiClient.get('/status/uploads/' + statusModalFile.FileID + '/agents', {
          withCredentials: true
        })
        .then(res => {
          setAssignedAgents(res.data || []);
        })
        .catch(err => {
          console.error('Failed to fetch assigned agents:', err);
          setAssignedAgents([]);
        });
    }
  }, [statusModalFile, isAdmin]);

  const selectedCategoryName = useMemo(() => {
    const match = categoryOptions.find(option => String(option.ID) === String(selectedCategoryId));
    return match?.Name || '';
  }, [categoryOptions, selectedCategoryId]);

  const selectedTypeName = useMemo(() => {
    const match = availableTypes.find(option => String(option.ID) === String(newTypeId));
    return match?.Name || '';
  }, [availableTypes, newTypeId]);

  const isIndustrialRendition = useMemo(() => (
    isAdmin
    && selectedCategoryName === 'Industrial'
    && RENDERED_ELIGIBLE_TYPES.includes(selectedTypeName)
  ), [isAdmin, selectedCategoryName, selectedTypeName]);

  const combinedOwners = useMemo(() => {
    const normalizedAssignedOwners = assignedOwners.map(owner => ({
      ownerKey: getOwnerKey(owner.partyNumber, owner.relationTypeId),
      partyNumber: owner.partyNumber,
      organizationName: owner.OrganizationName,
      cadName: owner.CADName,
      relationTypeId: owner.relationTypeId,
      relationTypeName: owner.relationTypeName,
      isTemporary: false
    }));

    const seenOwnerKeys = new Set(normalizedAssignedOwners.map(owner => owner.ownerKey));
    const normalizedSelectedOwners = owners
      .map(owner => ({
        ownerKey: getOwnerKey(owner.partyNumber, owner.relationTypeId),
        partyNumber: owner.partyNumber,
        organizationName: owner.organizationName,
        cadName: owner.cadName,
        relationTypeId: owner.relationTypeId,
        relationTypeName: relationTypes.find(rt => rt.ID === owner.relationTypeId)?.Name || owner.relationTypeId,
        isTemporary: true
      }))
      .filter(owner => {
        if (seenOwnerKeys.has(owner.ownerKey)) return false;
        seenOwnerKeys.add(owner.ownerKey);
        return true;
      });

    return [...normalizedAssignedOwners, ...normalizedSelectedOwners];
  }, [assignedOwners, owners, relationTypes]);

  useEffect(() => {
    renderedOwnersRef.current = ownerRenderedStates;
  }, [ownerRenderedStates]);

  useEffect(() => {
    if (!searchPartyNumber || !orgOptions.length) {
      setMatchedOrg(null);
      return;
    }

    // Search by PartyNbr (Id field from API)
    const match = orgOptions.find(o => o.Id.toString() === searchPartyNumber.toString());
    setMatchedOrg(match || null);
  }, [searchPartyNumber, orgOptions]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchPartyNumber);
    }, 300); // 300ms delay

    return () => {
      clearTimeout(handler); // Clean up if user types again
    };
  }, [searchPartyNumber]);
  // Debounce agent search term
  useEffect(() => {
    const id = setTimeout(() => setAgentDebouncedTerm(agentTerm.trim()), 400);
    return () => clearTimeout(id);
  }, [agentTerm]);

  useEffect(() => {
    const activeOwnerKeys = new Set(combinedOwners.map(owner => owner.ownerKey));
    setOwnerRenderedStates(prev => {
      const next = {};
      Object.keys(prev).forEach(ownerKey => {
        if (activeOwnerKeys.has(ownerKey)) {
          next[ownerKey] = prev[ownerKey];
        }
      });
      return Object.keys(next).length === Object.keys(prev).length ? prev : next;
    });
  }, [combinedOwners]);

  useEffect(() => {
    if (!statusModalFile || !isAdmin || !isIndustrialRendition) return;
    if (!combinedOwners.length) return;

    combinedOwners.forEach(owner => {
      const existingState = renderedOwnersRef.current[owner.ownerKey];
      if (existingState?.status === 'loading' || existingState?.status === 'ready' || existingState?.status === 'error') {
        return;
      }

      setOwnerRenderedStates(prev => ({
        ...prev,
        [owner.ownerKey]: {
          ownerKey: owner.ownerKey,
          ownerNbr: owner.partyNumber,
          ownerName: owner.organizationName,
          relationTypeId: owner.relationTypeId,
          relationTypeName: owner.relationTypeName,
          isTemporary: owner.isTemporary,
          status: 'loading',
          error: '',
          originalRendered: false,
          originalRenderedDate: '',
          originalExtended: false,
          rendered: false,
          renderedDate: '',
          extended: false
        }
      }));

      apiClient.get(`/status/uploads/${statusModalFile.FileID}/rendered`, {
        withCredentials: true,
        params: { ownerNbr: owner.partyNumber }
      })
        .then(res => {
          const data = res.data || {};
          const normalizedRendered = parseBooleanValue(data.Rendered ?? data.rendered);
          const normalizedExtended = parseBooleanValue(data.Extended ?? data.extended);
          const normalizedRenderedDate = normalizedRendered
            ? normalizeDateValue(data.RenderedDate ?? data.renderedDate)
            : '';

          setOwnerRenderedStates(prev => ({
            ...prev,
            [owner.ownerKey]: {
              ownerKey: owner.ownerKey,
              ownerNbr: owner.partyNumber,
              ownerName: owner.organizationName,
              relationTypeId: owner.relationTypeId,
              relationTypeName: owner.relationTypeName,
              isTemporary: owner.isTemporary,
              status: 'ready',
              error: '',
              originalRendered: normalizedRendered,
              originalRenderedDate: normalizedRenderedDate,
              originalExtended: normalizedExtended,
              rendered: normalizedRendered,
              renderedDate: normalizedRenderedDate,
              extended: normalizedExtended
            }
          }));
        })
        .catch(err => {
          setOwnerRenderedStates(prev => ({
            ...prev,
            [owner.ownerKey]: {
              ownerKey: owner.ownerKey,
              ownerNbr: owner.partyNumber,
              ownerName: owner.organizationName,
              relationTypeId: owner.relationTypeId,
              relationTypeName: owner.relationTypeName,
              isTemporary: owner.isTemporary,
              status: 'error',
              error: err.response?.data?.error || 'Rendered info was not found for this owner.',
              originalRendered: false,
              originalRenderedDate: '',
              originalExtended: false,
              rendered: false,
              renderedDate: '',
              extended: false
            }
          }));
        });
    });
  }, [combinedOwners, isAdmin, isIndustrialRendition, statusModalFile]);

  // Fetch agents by name/legacy code with debounced term
  useEffect(() => {
    if (!agentDebouncedTerm) { setAgentResults([]); return; }
    apiClient.get("/lookup/AgentNames", {
      withCredentials: true,
      params: { term: agentDebouncedTerm, limit: 50 }
    })
    .then(res => setAgentResults(res.data || []))
    .catch(() => setAgentResults([]));
  }, [agentDebouncedTerm]);


  useEffect(() => {
    if (!debouncedSearch || !orgOptions.length) {
      setSuggestedOrgs([]);
      setMatchedOrg(null);
      return;
    }

    const input = debouncedSearch.toLowerCase();

    // Match by party number or partial name
    const matches = orgOptions.filter(org =>
      org.Id.toString().includes(input) ||
      org.OrganizationName.toLowerCase().includes(input)
    );

    setSuggestedOrgs(matches.slice(0, 10)); // Limit to top 5
  }, [debouncedSearch, orgOptions]);

  const handleCategoryChange = async (event) => {
    const nextCategoryId = toNumberOrBlank(event.target.value);
    const currentCategoryKey = String(selectedCategoryId || '');
    if (currentCategoryKey) {
      categoryDraftTypeRef.current[currentCategoryKey] = newTypeId || '';
    }

    setSelectedCategoryId(nextCategoryId);
    setNewTypeId('');
    setTypeError(nextCategoryId ? 'Attachment Type is required.' : '');
    setAvailableTypes([]);

    if (!nextCategoryId) return;

    try {
      const typeRes = await apiClient.get(`/lookup/types/${nextCategoryId}`, { withCredentials: true });
      const nextTypes = typeRes.data || [];
      setAvailableTypes(nextTypes);

      const rememberedType = categoryDraftTypeRef.current[String(nextCategoryId)];
      const fallbackType = String(nextCategoryId) === String(originalCategoryId) ? originalTypeId : '';
      const candidateType = rememberedType || fallbackType;

      if (candidateType && nextTypes.some(type => String(type.ID) === String(candidateType))) {
        setNewTypeId(candidateType);
        setTypeError('');
      }
    } catch (typeErr) {
      console.error('Failed to fetch types for category change:', typeErr);
      setAvailableTypes([]);
    }
  };

  const handleTypeChange = (event) => {
    const nextTypeId = toNumberOrBlank(event.target.value);
    setNewTypeId(nextTypeId);
    setTypeError(nextTypeId ? '' : 'Attachment Type is required.');

    if (selectedCategoryId) {
      categoryDraftTypeRef.current[String(selectedCategoryId)] = nextTypeId || '';
    }
  };

  const handleRenderedOwnerChange = (ownerKey, field, value) => {
    setOwnerRenderedStates(prev => {
      const current = prev[ownerKey];
      if (!current) return prev;

      const nextState = { ...current };
      if (field === 'rendered') {
        nextState.rendered = value;
        if (!value) {
          nextState.renderedDate = '';
        } else if (!nextState.renderedDate) {
          nextState.renderedDate = current.originalRenderedDate || '';
        }
      } else if (field === 'renderedDate') {
        nextState.renderedDate = value;
      } else if (field === 'extended') {
        nextState.extended = value;
      }

      return {
        ...prev,
        [ownerKey]: nextState
      };
    });
  };


  const addOwner = () => {
    if (!matchedOrg || !selectedRelationTypeId) {
      alert('Please select both an organization and relation type');
      return;
    }

    const ownerKey = getOwnerKey(matchedOrg.Id, parseInt(selectedRelationTypeId, 10));
    const exists = combinedOwners.some(owner => owner.ownerKey === ownerKey);
    if (exists) {
      alert('This organization is already added');
      return;
    }

    const newOwner = {
      partyNumber: matchedOrg.Id,
      organizationName: matchedOrg.OrganizationName,
      cadName: matchedOrg.Name,
      relationTypeId: parseInt(selectedRelationTypeId)
    };

    setOwners(prev => {
      return [...prev, newOwner];
    });

    // Clear search fields
    setSearchPartyNumber('');
    setDebouncedSearch('');
    setSuggestedOrgs([]);
    setMatchedOrg(null);

    alert(`Owner "${matchedOrg.OrganizationName}" added successfully! You can Link more owners or click Save to apply changes.`);
  };

  // Unlink agent
  const handleUnlinkAgent = async (agentNbr, relationTypeId) => {
    if (!statusModalFile || !agentNbr || !relationTypeId) return;

    setIsUnlinkingAgent(true);
    try {
      await apiClient.delete(
        `/status/uploads/${statusModalFile.FileID}/agents/${agentNbr}/${relationTypeId}`,
        { withCredentials: true }
      );

      // Remove from local state
      setAssignedAgents(prev =>
        prev.filter(a => !(a.AgentNbr === agentNbr && a.relationTypeId === relationTypeId))
      );

      setUnlinkingAgentKey(null);
    } catch (err) {
      console.error('Failed to unlink agent:', err);
      alert('Failed to unlink agent. Please try again.');
    } finally {
      setIsUnlinkingAgent(false);
    }
  };

  const removeOwner = (ownerKey) => {
    setOwners(prev => prev.filter(o => getOwnerKey(o.partyNumber, o.relationTypeId) !== ownerKey));
  };

  const handleUnlinkOwner = async (partyNumber, relationTypeId) => {
    if (!statusModalFile) return;

    setIsUnlinking(true);
    try {
      await apiClient.delete(
        `/status/uploads/${statusModalFile.FileID}/owners/${partyNumber}/${relationTypeId}`,
        { withCredentials: true }
      );

      // Remove from local state
      setAssignedOwners(prev =>
        prev.filter(o => !(o.partyNumber === partyNumber && o.relationTypeId === relationTypeId))
      );

      setUnlinkingOwnerId(null);
    } catch (err) {
      console.error('Failed to unlink owner:', err);
      alert('Failed to unlink owner. Please try again.');
    } finally {
      setIsUnlinking(false);
    }
  };


  const buildRenderedPayload = (renderState) => {
    if (!renderState) return null;

    const normalizedRenderedDate = renderState.rendered ? (renderState.renderedDate || '') : '';
    const payload = {};

    if (renderState.originalRendered !== renderState.rendered) {
      payload.rendered = renderState.rendered;
      if (!renderState.rendered) {
        payload.renderedDate = null;
      }
    }

    if (renderState.originalRenderedDate !== normalizedRenderedDate) {
      payload.renderedDate = normalizedRenderedDate || null;
    }

    if (renderState.originalExtended !== renderState.extended) {
      payload.extended = renderState.extended;
    }

    return Object.keys(payload).length > 0 ? payload : null;
  };

  const handleSave = async () => {
    const fileId = statusModalFile.FileID;
    const ownersPayload = isAdmin ? owners : [];
    const renderedOwners = combinedOwners.map(owner => ({
      ...owner,
      renderState: ownerRenderedStates[owner.ownerKey]
    }));

    if (document.activeElement) {
      document.activeElement.blur();
    }

    setError('');

    if (!description.trim()) {
      showModalError('Description is required.');
      return;
    }

    if (description.trim().split(/\s+/).length > 100) {
      showModalError('Description exceeds 100 word limit.');
      return;
    }

    if (!selectedCategoryId) {
      showModalError('Attachment Category is required.');
      return;
    }

    if (!newTypeId) {
      setTypeError('Attachment Type is required.');
      showModalError('Attachment Type is required.');
      return;
    }

    if (selectedAgents.length > 0 && !agentRelationTypeId) {
      showModalError('Select an agent relation type before saving agents.');
      return;
    }

    if (isAdmin && isIndustrialRendition) {
      const renderedLoadFailure = renderedOwners.find(owner => owner.renderState?.status === 'error');
      if (renderedLoadFailure) {
        showModalError(`Rendered info could not be loaded for ${renderedLoadFailure.organizationName}.`);
        return;
      }

      const missingRenderedDate = renderedOwners.find(owner => {
        const renderState = owner.renderState;
        return renderState?.status === 'ready' && renderState.rendered && !renderState.renderedDate;
      });

      if (missingRenderedDate) {
        showModalError(`Rendered Date is required for ${missingRenderedDate.organizationName}.`);
        return;
      }
    }

    if (saveInProgressRef.current) {
      return;
    }

    try {
      saveInProgressRef.current = true;
      setIsSaving(true);
      const agentsPayload = isAdmin
        ? selectedAgents.map(agent => ({
            agentName: agent.AgentName,
            legacyCode: agent.LegacyCode || null,
            relationTypeId: parseInt(agentRelationTypeId, 10)
          }))
        : [];
      const renderedOwnersPayload = isAdmin && isIndustrialRendition
        ? renderedOwners
            .map(owner => {
              const payload = buildRenderedPayload(owner.renderState);
              return payload ? { ownerNbr: owner.partyNumber, ...payload } : null;
            })
            .filter(Boolean)
        : [];

      const saveResponse = await apiClient.patch(
        `/status/uploads/${fileId}/save`,
        {
          statusId: newStatusId,
          description: description.trim(),
          attachmentCategoryId: selectedCategoryId,
          attachmentTypeId: newTypeId,
          owners: ownersPayload,
          agents: agentsPayload,
          renderedOwners: renderedOwnersPayload
        },
        { withCredentials: true }
      );

      const successMessage = saveResponse?.data?.message || 'Status updated successfully!';

      alert(successMessage);

      setStatusModalFile(null);

      if (typeof handleStatusUpdate === 'function') {
        try {
          await handleStatusUpdate();
        } catch (refreshErr) {
          console.error('Failed to refresh files after status update:', refreshErr);
        }
      }
    } catch (err) {
      console.error('Error saving status:', err);
      showModalError(err.response?.data?.error || err.message || 'Failed to save status');
    } finally {
      setIsSaving(false);
      saveInProgressRef.current = false;
    }
  };


  if (!statusModalFile) return null;
  return (
    <div style={modalStyles.overlay}>
      <div style={modalStyles.box}>
        {/* Header Section */}
        <div style={modalStyles.headerSection}>
          <h3 style={modalStyles.modalTitle}>Edit</h3>
          <button
            onClick={() => setStatusModalFile(null)}
            style={modalStyles.closeButton}
          >
            ×
          </button>
        </div>

        {/* Content Area */}
        <div ref={contentAreaRef} style={modalStyles.contentArea} className="modal-content-area">
          <div style={modalStyles.contentContainer}>
            
            {/* File Info Section */}
            <div style={modalStyles.fileInfoSection}>
              <div style={modalStyles.fileInfoItem}>
                <label style={modalStyles.fileInfoLabel}>File ID</label>
                <div style={modalStyles.fileInfoValue}>{statusModalFile.FileID}</div>
              </div>
              <div style={modalStyles.fileInfoItem}>
                <label style={modalStyles.fileInfoLabel}>File Name</label>
                <div
                  style={{
                    ...modalStyles.fileInfoValue,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'normal',
                    display: '-webkit-box',
                    WebkitBoxOrient: 'vertical',
                    WebkitLineClamp: 2,
                    lineHeight: '1.35',
                    maxHeight: '2.7em',
                    overflowWrap: 'anywhere'
                  }}
                  title={statusModalFile.FileName}
                >
                  {statusModalFile.FileName}
                </div>
              </div>
              {countyName && (
                <div style={modalStyles.fileInfoItem}>
                  <label style={modalStyles.fileInfoLabel}>County</label>
                  <div style={modalStyles.fileInfoValue}>{countyName}</div>
                </div>
              )}
            </div>

            {/* Error Message */}
            {error && (
              <div style={modalStyles.errorMessage}>
                {error}
              </div>
            )}

            {/* Loading Message */}
            {loading && (
              <div style={modalStyles.loadingMessage}>
                Loading organization data...
              </div>
            )}

            {/* Form Sections Container */}
            <div style={modalStyles.formSectionsContainer} className="form-sections-container">
              
              {/* Status Section */}
              <div style={modalStyles.formSection}>
                <h4 style={modalStyles.sectionTitle}>Status</h4>
                <label htmlFor={statusSelectId} style={modalStyles.formLabel}> Status:</label>
                <select
                  id={statusSelectId}
                  name="status"
                  value={newStatusId}
                  onChange={(e) => setNewStatusId(parseInt(e.target.value))}
                  style={modalStyles.modalSelect}
                >
                  {statusOptions.map(status => (
                    <option key={status.id} value={status.id}>{status.label}</option>
                  ))}
                </select>
              </div>

              <div style={modalStyles.formSection}>
                <h4 style={modalStyles.sectionTitle}>Category</h4>
                <label htmlFor={categorySelectId} style={modalStyles.formLabel}> Attachment Category:</label>
                <select
                  id={categorySelectId}
                  name="attachmentCategory"
                  value={selectedCategoryId}
                  onChange={handleCategoryChange}
                  style={modalStyles.modalSelect}
                >
                  <option value="">Select a category</option>
                  {categoryOptions.map(category => (
                    <option key={category.ID} value={category.ID}>{category.Name}</option>
                  ))}
                </select>
              </div>

              <div style={modalStyles.formSection}>
                <h4 style={modalStyles.sectionTitle}>Attachment Type</h4>
                <label htmlFor={attachmentTypeSelectId} style={modalStyles.formLabel}> Attachment Type:</label>
                <select
                  id={attachmentTypeSelectId}
                  name="attachmentType"
                  value={newTypeId}
                  onChange={handleTypeChange}
                  style={{
                    ...modalStyles.modalSelect,
                    borderColor: typeError ? '#dc2626' : modalStyles.modalSelect.borderColor
                  }}
                >
                  <option value="">Select a type</option>
                  {availableTypes.map(type => (
                    <option key={type.ID} value={type.ID}>{type.Name}</option>
                  ))}
                </select>
                {typeError && (
                  <div style={{ color: '#dc2626', fontSize: '13px', fontWeight: 600, marginTop: '8px' }}>
                    {typeError}
                  </div>
                )}
              </div>
            </div>


            {/* Description Section - Full Width */}
            <div style={modalStyles.fullWidthSection}>
              <h4 style={modalStyles.sectionTitle}>Description</h4>
              <label htmlFor={descriptionTextareaId} style={modalStyles.formLabel}>
                Edit Description (max 100 words):
              </label>
              <textarea
                id={descriptionTextareaId}
                name="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                style={modalStyles.modalTextarea}
                maxLength={1000}
                placeholder="Enter file description..."
              />
            </div>

            {isAdmin && (
              <>
            <div style={modalStyles.fullWidthSection}>
              <h4 style={modalStyles.sectionTitle}>Owner</h4> 
              
              {/* Updated to use vertical stacking */}
              <div style={modalStyles.ownerFieldsContainer}>
                {/* Relation Type Selection */}
                <div style={modalStyles.ownerFieldGroup}>
                  <label htmlFor={ownerRelationTypeSelectId} style={modalStyles.formLabel}>Select Relation Type:</label>
                  <select
                    id={ownerRelationTypeSelectId}
                    name="ownerRelationType"
                    value={selectedRelationTypeId}
                    onChange={(e) => setSelectedRelationTypeId(e.target.value)}
                    style={modalStyles.modalSelect}
                    disabled={loading}
                  >
                    <option value="" disabled>Select Relation Type</option>
                    {relationTypes.map(rt => (
                      <option key={rt.ID} value={rt.ID}>{rt.Name}</option>
                    ))}
                  </select>
                </div>

                {/* Search Organization */}
                <div style={modalStyles.ownerFieldGroup}>
                  <label htmlFor={partyNumberInputId} style={modalStyles.formLabel}>Search by Party Number:</label>
                  <input
                    id={partyNumberInputId}
                    name="partyNumberSearch"
                    type="text"
                    placeholder="Enter organization name or party number"
                    value={searchPartyNumber}
                    onChange={e => setSearchPartyNumber(e.target.value)}
                    style={modalStyles.modalInput}
                    disabled={loading}
                  />

                  {/* Search suggestions will appear below the input */}
                  {suggestedOrgs.length > 0 && (
                    <ul style={modalStyles.suggestionsList}>
                      {suggestedOrgs.map(org => (
                        <li
                          key={org.Id}
                          style={modalStyles.suggestionItem}
                          onClick={() => {
                            setSearchPartyNumber(org.Id.toString());
                            setMatchedOrg(org);
                            setSuggestedOrgs([]);
                          }}
                        >
                          <strong>{org.OrganizationName}</strong> — Party Num : {org.Id}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>

              {/* Matched Organization Display */}
              {matchedOrg && (
                <div style={{
                  ...modalStyles.ownerCard,
                  background: 'linear-gradient(135deg, #e8f5e8 0%, #f0fff0 100%)',
                  border: '2px solid rgba(34, 197, 94, 0.3)'
                }}>
                  <div style={{ marginBottom: '15px' }}>
                    <h5 style={{ 
                      ...modalStyles.sectionTitle, 
                      fontSize: '16px', 
                      marginBottom: '10px',
                      color: '#059669'
                    }}>
                      Matched Organization
                    </h5>
                    <p style={{ margin: '5px 0', fontSize: '14px' }}>
                      <strong>Party Number:</strong> {matchedOrg.Id}
                    </p>
                    <p style={{ margin: '5px 0', fontSize: '14px' }}>
                      <strong>Organization:</strong> {matchedOrg.OrganizationName}
                    </p>
                    <p style={{ margin: '5px 0', fontSize: '14px' }}>
                      <strong>CAD Name:</strong> {matchedOrg.Name}
                    </p>
                  </div>
                  <button 
                    onClick={addOwner}
                    style={{
                      ...modalStyles.saveButton,
                      background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                      padding: '12px 14px',
                      fontSize: '14px',
                      minWidth: 'auto'
                    }}
                    disabled={!selectedRelationTypeId}
                  >
                    Link Owner
                  </button>
                </div>
              )}

              {searchPartyNumber && !matchedOrg && orgOptions.length > 0 && (
                <div style={{
                  ...modalStyles.errorMessage,
                  background: 'rgba(251, 191, 36, 0.1)',
                  borderColor: 'rgba(251, 191, 36, 0.3)',
                  color: '#92400e'
                }}>
                  No organization found with Party Number: {searchPartyNumber}
                </div>
              )}

              {/* Selected Owners */}
              {owners.length > 0 && (
                <div style={{ marginTop: '30px' }}>
                  <h5 style={modalStyles.sectionTitle}>Selected Owners:</h5>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {owners.map(owner => (
                      <div key={getOwnerKey(owner.partyNumber, owner.relationTypeId)} style={{
                        ...modalStyles.ownerCard,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '20px'
                      }}>
                        <div style={{ flex: 1 }}>
                          <p style={{ margin: '0 0 5px 0', fontSize: '14px' }}>
                            <strong>Party #:</strong> {owner.partyNumber} | 
                            <strong> Org:</strong> {owner.organizationName}
                          </p>
                          <p style={{ margin: '0', fontSize: '12px', color: '#6b7280' }}>
                            <strong>CAD:</strong> {owner.cadName} | 
                            <strong> Relation:</strong> {relationTypes.find(rt => rt.ID === owner.relationTypeId)?.Name || owner.relationTypeId}
                          </p>
                        </div>
                        <button 
                          onClick={() => removeOwner(getOwnerKey(owner.partyNumber, owner.relationTypeId))}
                          style={{
                            background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '50%',
                            width: '36px',
                            height: '36px',
                            cursor: 'pointer',
                            fontSize: '18px',
                            fontWeight: 'bold',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'all 0.3s ease',
                            boxShadow: '0 4px 12px rgba(220, 38, 38, 0.3)'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.transform = 'scale(1.1)';
                            e.target.style.boxShadow = '0 6px 20px rgba(220, 38, 38, 0.4)';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.transform = 'scale(1)';
                            e.target.style.boxShadow = '0 4px 12px rgba(220, 38, 38, 0.3)';
                          }}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Assigned Owners */}
              <div style={{ marginTop: '30px' }}>
                <h5 style={modalStyles.sectionTitle}>Assigned Owners:</h5>
                {assignedOwners.length === 0 ? (
                  <p style={{ 
                    color: '#6b7280', 
                    fontStyle: 'italic',
                    textAlign: 'center',
                    padding: '20px',
                    background: 'rgba(0, 119, 182, 0.05)',
                    borderRadius: '12px'
                  }}>
                    Not assigned yet
                  </p>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                    {assignedOwners.map((o, i) => {
                      const isConfirming = unlinkingOwnerId === `${o.partyNumber}_${o.relationTypeId}`;
                      return (
                        <div
                          key={`${o.partyNumber}_${o.relationTypeId}`}
                          style={{
                            ...modalStyles.ownerCard,
                            background: isConfirming 
                              ? 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)' 
                              : 'linear-gradient(135deg, #f8fcff 0%, #ffffff 100%)',
                            border: isConfirming 
                              ? '2px solid rgba(239, 68, 68, 0.3)' 
                              : '2px solid rgba(0, 119, 182, 0.2)'
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <div style={{ flex: 1 }}>
                              <p style={{ margin: '0 0 8px 0', fontSize: '16px', fontWeight: '600' }}>
                                <strong>Org Name:</strong> {o.OrganizationName}
                              </p>
                              <p style={{ margin: '0', fontSize: '14px', color: '#6b7280' }}>
                                Party Number: {o.partyNumber} | CAD: {o.CADName} | RelationType: {o.relationTypeName}
                              </p>
                            </div>
                            <button
                              title="Remove owner"
                              onClick={() =>
                                setUnlinkingOwnerId(
                                  isConfirming ? null : `${o.partyNumber}_${o.relationTypeId}`
                                )
                              }
                              style={{
                                background: 'none',
                                border: 'none',
                                color: '#dc2626',
                                cursor: 'pointer',
                                fontWeight: 'bold',
                                fontSize: '24px',
                                padding: '4px',
                                borderRadius: '4px',
                                transition: 'all 0.2s ease'
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.background = 'rgba(220, 38, 38, 0.1)';
                                e.target.style.transform = 'scale(1.1)';
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.background = 'none';
                                e.target.style.transform = 'scale(1)';
                              }}
                            >
                            ×
                            </button>
                          </div>

                          {isConfirming && (
                            <div style={{
                              marginTop: '20px',
                              padding: '20px',
                              background: 'rgba(239, 68, 68, 0.05)',
                              border: '1px solid rgba(239, 68, 68, 0.2)',
                              borderRadius: '12px'
                            }}>
                              <p style={{ 
                                marginBottom: '15px', 
                                color: '#dc2626',
                                fontWeight: '600',
                                fontSize: '14px'
                              }}>
                                Are you sure you want to unlink this owner?
                              </p>
                              <div style={{ display: 'flex', gap: '12px' }}>
                                <button
                                  onClick={() => handleUnlinkOwner(o.partyNumber, o.relationTypeId)}
                                  disabled={isUnlinking}
                                  style={{
                                    background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                                    color: 'white',
                                    border: 'none',
                                    padding: '10px 20px',
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    opacity: isUnlinking ? 0.6 : 1,
                                    fontWeight: '600',
                                    fontSize: '14px',
                                    transition: 'all 0.3s ease'
                                  }}
                                >
                                  {isUnlinking ? 'Unlinking...' : 'Confirm'}
                                </button>
                                <button
                                  onClick={() => setUnlinkingOwnerId(null)}
                                  style={{
                                    background: 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)',
                                    color: 'white',
                                    border: 'none',
                                    padding: '10px 20px',
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    fontWeight: '600',
                                    fontSize: '14px',
                                    transition: 'all 0.3s ease'
                                  }}
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Rendered section — appears inside Owner after Link Owner is clicked or on open with existing owners */}
              {isIndustrialRendition && (
                <div style={{ marginTop: 28, borderTop: '1px solid #e5e7eb', paddingTop: 20 }}>
                  <h4 style={modalStyles.sectionTitle}>Rendered By Owner</h4>

                  {combinedOwners.length === 0 && (
                    <p style={{
                      color: '#6b7280',
                      fontStyle: 'italic',
                      textAlign: 'center',
                      padding: '18px',
                      background: 'rgba(0, 119, 182, 0.05)',
                      borderRadius: '12px'
                    }}>
                      Link an owner to load rendered info.
                    </p>
                  )}

                  {combinedOwners.length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                      {combinedOwners.map(owner => {
                        const renderState = ownerRenderedStates[owner.ownerKey];
                        const renderedInputId = `rendered-${owner.ownerKey}`;
                        const extendedInputId = `extended-${owner.ownerKey}`;
                        const ownerRenderedDateInputId = `rendered-date-${owner.ownerKey}`;

                        return (
                          <div
                            key={owner.ownerKey}
                            style={{
                              ...modalStyles.ownerCard,
                              background: 'linear-gradient(135deg, #f8fcff 0%, #ffffff 100%)',
                              border: '2px solid rgba(0, 119, 182, 0.18)'
                            }}
                          >
                            <div style={{ marginBottom: '14px' }}>
                              <p style={{ margin: '0 0 6px 0', fontSize: '16px', fontWeight: 700, color: '#1e3a8a' }}>
                                Rendered - {owner.organizationName}
                              </p>
                              <p style={{ margin: 0, fontSize: '13px', color: '#6b7280' }}>
                                Party Number: {owner.partyNumber} | Relation Type: {owner.relationTypeName || owner.relationTypeId}
                                {owner.isTemporary ? ' | Pending owner link' : ''}
                              </p>
                            </div>

                            {!renderState || renderState.status === 'loading' ? (
                              <div style={modalStyles.loadingMessage}>Loading rendered info...</div>
                            ) : renderState.status === 'error' ? (
                              <div style={modalStyles.errorMessage}>{renderState.error}</div>
                            ) : (
                              <>
                                <div style={{ marginBottom: '16px' }}>
                                  <p style={{ fontWeight: 600, margin: '0 0 6px 0' }}>
                                    Current Rendered Date:{' '}
                                    <span style={{ color: '#0077b6' }}>
                                      {renderState.originalRenderedDate || 'Not set'}
                                    </span>
                                  </p>
                                  <p style={{ fontWeight: 600, margin: 0 }}>
                                    Current Extended:{' '}
                                    <span style={{ color: renderState.originalExtended ? '#059669' : '#b91c1c' }}>
                                      {renderState.originalExtended ? 'Yes' : 'No'}
                                    </span>
                                  </p>
                                </div>

                                <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap', marginBottom: '20px' }}>
                                  <label htmlFor={renderedInputId} style={{ fontWeight: 600, fontSize: '16px', display: 'flex', alignItems: 'center' }}>
                                    <input
                                      id={renderedInputId}
                                      name={renderedInputId}
                                      type="checkbox"
                                      checked={renderState.rendered}
                                      onChange={event => handleRenderedOwnerChange(owner.ownerKey, 'rendered', event.target.checked)}
                                      style={{ marginRight: '10px' }}
                                    />
                                    Rendered
                                  </label>
                                  <label htmlFor={extendedInputId} style={{ fontWeight: 600, fontSize: '16px', display: 'flex', alignItems: 'center' }}>
                                    <input
                                      id={extendedInputId}
                                      name={extendedInputId}
                                      type="checkbox"
                                      checked={renderState.extended}
                                      onChange={event => handleRenderedOwnerChange(owner.ownerKey, 'extended', event.target.checked)}
                                      style={{ marginRight: '10px' }}
                                    />
                                    Extended
                                  </label>
                                </div>

                                {renderState.rendered && (
                                  <>
                                    <label htmlFor={ownerRenderedDateInputId} style={modalStyles.formLabel}>Rendered Date:</label>
                                    <input
                                      id={ownerRenderedDateInputId}
                                      name={ownerRenderedDateInputId}
                                      type="date"
                                      value={renderState.renderedDate}
                                      onChange={event => handleRenderedOwnerChange(owner.ownerKey, 'renderedDate', event.target.value)}
                                      style={{
                                        ...modalStyles.modalInput,
                                        borderColor: !renderState.renderedDate ? '#dc2626' : modalStyles.modalInput.borderColor
                                      }}
                                    />
                                    {!renderState.renderedDate && (
                                      <div style={{ color: '#dc2626', fontSize: '13px', fontWeight: 600, marginTop: '8px' }}>
                                        Rendered Date is required when Rendered is checked.
                                      </div>
                                    )}
                                    <div style={{ marginTop: '10px' }}>
                                      <button
                                        onClick={() => handleRenderedOwnerChange(owner.ownerKey, 'renderedDate', `${new Date().getFullYear()}-04-15`)}
                                        style={{ ...modalStyles.saveButton, minWidth: 'auto', padding: '10px 20px', marginRight: '15px' }}
                                      >
                                        Set 04/15
                                      </button>
                                      <button
                                        onClick={() => handleRenderedOwnerChange(owner.ownerKey, 'renderedDate', `${new Date().getFullYear()}-05-15`)}
                                        style={{ ...modalStyles.saveButton, minWidth: 'auto', padding: '10px 20px' }}
                                      >
                                        Set 05/15
                                      </button>
                                    </div>
                                  </>
                                )}
                              </>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
             {/* Agent Assignment (Full Width) */}
            <div style={modalStyles.fullWidthSection}>
              <h4 style={modalStyles.sectionTitle}>Agent</h4>

              <div style={modalStyles.ownerFieldsContainer}>
                <div style={modalStyles.ownerFieldGroup}>
                  <label htmlFor={agentRelationTypeSelectId} style={modalStyles.formLabel}>Select Relation Type:</label>
                  <select
                    id={agentRelationTypeSelectId}
                    name="agentRelationType"
                    value={agentRelationTypeId}
                    onChange={(e) => setAgentRelationTypeId(e.target.value)}
                    style={modalStyles.modalSelect}
                  >
                    <option value="">Select Relation Type</option>
                    {relationTypes.map(rt => (
                      <option key={rt.ID} value={rt.ID}>{rt.Name}</option>
                    ))}
                  </select>
                </div>

                <div style={modalStyles.ownerFieldGroup}>
                  <label htmlFor={agentSearchInputId} style={modalStyles.formLabel}>Search Agent (name or legacy code):</label>
                  <input
                    id={agentSearchInputId}
                    name="agentSearchTerm"
                    type="text"
                    placeholder="Type agent name or legacy code"
                    value={agentTerm}
                    onChange={e => setAgentTerm(e.target.value)}
                    style={modalStyles.modalInput}
                  />

                  {agentResults.length > 0 && selectedAgents.length === 0 && (
                    <ul style={modalStyles.suggestionsList}>
                      {agentResults.map((a, idx) => (
                        <li
                          key={idx}
                          style={modalStyles.suggestionItem}
                          onClick={() => {
                            setSelectedAgents(prev => prev.some(x => x.AgentName === a.AgentName && (x.LegacyCode || '') === (a.LegacyCode || '')) ? prev : [...prev, a]);
                            setAgentResults([]);
                            setAgentTerm('');
                          }}
                        >
                          <div style={{ fontWeight: 600 }}>{a.AgentName}</div>
                          <div style={{ fontSize: '12px', color: '#6b7280' }}>Legacy Code: {a.LegacyCode || '(none)'}</div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>

              {selectedAgents.length > 0 && (
                <div style={{
                  marginTop: '16px',
                  padding: '12px',
                  border: '1px solid rgba(0,0,0,0.08)',
                  borderRadius: '8px',
                  background: 'linear-gradient(135deg, #f8fcff 0%, #ffffff 100%)'
                }}>
                  <h5 style={{
                    ...modalStyles.sectionTitle,
                    fontSize: '16px',
                    marginBottom: '8px',
                    color: '#0369a1'
                  }}>
                    Selected Agents
                  </h5>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {selectedAgents.map((a, idx) => (
                      <div key={`${a.AgentName}_${a.LegacyCode || 'NA'}_${idx}`} style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '8px 10px',
                        borderRadius: '999px',
                        border: '1px solid rgba(3, 105, 161, 0.25)',
                        background: 'rgba(3, 105, 161, 0.06)'
                      }}>
                        <span style={{ fontWeight: 600 }}>{a.AgentName}</span>
                        <span style={{ fontSize: '12px', color: '#6b7280' }}>
                          {a.LegacyCode ? `(Legacy: ${a.LegacyCode})` : ''}
                        </span>
                        <button
                          title="Remove agent"
                          onClick={() => setSelectedAgents(prev => prev.filter((_, i) => i !== idx))}
                          style={{
                            background: 'transparent',
                            border: 'none',
                            color: '#dc2626',
                            cursor: 'pointer',
                            fontWeight: 'bold'
                          }}
                        >
                          x
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

                 {/* Assigned Agents */}
              <div style={{ marginTop: '30px' }}>
                <h5 style={modalStyles.sectionTitle}>Assigned Agents:</h5>
                {assignedAgents.length === 0 ? (
                  <p style={{ 
                    color: '#6b7280', 
                    fontStyle: 'italic',
                    textAlign: 'center',
                    padding: '20px',
                    background: 'rgba(3, 105, 161, 0.05)',
                    borderRadius: '12px'
                  }}>
                    Not assigned yet
                  </p>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                    {assignedAgents.map((a, i) => {
                      const agentKey = `${a.AgentNbr}_${a.relationTypeId}`;
                      const isConfirming = unlinkingAgentKey === agentKey;
                      return (
                        <div
                          key={`${a.AgentNbr || 'A'}_${a.relationTypeId || 'R'}_${i}`}
                          style={{
                            ...modalStyles.ownerCard,
                            background: isConfirming
                              ? 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)'
                              : 'linear-gradient(135deg, #f8fcff 0%, #ffffff 100%)',
                            border: isConfirming
                              ? '2px solid rgba(239, 68, 68, 0.3)'
                              : '2px solid rgba(3, 105, 161, 0.2)'
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <div style={{ flex: 1 }}>
                              <p style={{ margin: '0 0 8px 0', fontSize: '16px', fontWeight: '600' }}>
                                <strong>Agent:</strong> {a.OrganizationName}
                              </p>
                              <p style={{ margin: 0, fontSize: '14px', color: '#6b7280' }}>
                                Agent Nbr: {a.AgentNbr} | Legacy Code: {a.LegacyCode || 'N/A'} | Relation Type: {a.relationTypeName || a.relationTypeId}
                              </p>
                              <p style={{ margin: 0, fontSize: '12px', color: '#6b7280' }}>
                                {(a.Address1 || '')} {(a.Address2 || '')} {(a.Address3 || '')}
                                {a.City ? ` - ${a.City},` : ''} { a.StateName || ''} ({(a.StateAbbreviation ) || ''}) {a.PostalCode || ''}
                              </p>
                            </div>
                            <button
                              title="Remove agent"
                              onClick={() => setUnlinkingAgentKey(isConfirming ? null : agentKey)}
                              style={{
                                background: 'none',
                                border: 'none',
                                color: '#dc2626',
                                cursor: 'pointer',
                                fontWeight: 'bold',
                                fontSize: '20px',
                                padding: '4px',
                                borderRadius: '4px',
                                transition: 'all 0.2s ease'
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.background = 'rgba(220, 38, 38, 0.1)';
                                e.target.style.transform = 'scale(1.1)';
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.background = 'none';
                                e.target.style.transform = 'scale(1)';
                              }}
                            >
                             x
                            </button>
                          </div>

                          {isConfirming && (
                            <div style={{
                              marginTop: '20px',
                              padding: '20px',
                              background: 'rgba(239, 68, 68, 0.05)',
                              border: '1px solid rgba(239, 68, 68, 0.2)',
                              borderRadius: '12px'
                            }}>
                              <p style={{ 
                                marginBottom: '15px', 
                                color: '#dc2626',
                                fontWeight: '600',
                                fontSize: '14px'
                              }}>
                                Are you sure you want to unlink this agent?
                              </p>
                              <div style={{ display: 'flex', gap: '12px' }}>
                                <button
                                  onClick={() => handleUnlinkAgent(a.AgentNbr, a.relationTypeId)}
                                  disabled={isUnlinkingAgent}
                                  style={{
                                    background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                                    color: 'white',
                                    border: 'none',
                                    padding: '10px 20px',
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    opacity: isUnlinkingAgent ? 0.6 : 1,
                                    fontWeight: '600',
                                    fontSize: '14px',
                                    transition: 'all 0.3s ease'
                                  }}
                                >
                                  {isUnlinkingAgent ? 'Unlinking...' : 'Confirm'}
                                </button>
                                <button
                                  onClick={() => setUnlinkingAgentKey(null)}
                                  style={{
                                    background: 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)',
                                    color: 'white',
                                    border: 'none',
                                    padding: '10px 20px',
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    fontWeight: '600',
                                    fontSize: '14px',
                                    transition: 'all 0.3s ease'
                                  }}
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
              </>
            )}
            <div style={modalStyles.actionsSection}>
            <button
              onClick={handleSave}
              onDoubleClick={(e) => e.preventDefault()}
              style={{
                ...modalStyles.saveButton,
                opacity: (isSaving || isSavingStatus) ? 0.6 : 1,
                cursor: (isSaving || isSavingStatus) ? 'not-allowed' : 'pointer'
              }}
              disabled={isSaving || isSavingStatus}
            >
              {(isSaving || isSavingStatus) ? 'Saving...' : 'Save'}
            </button>
            

           
            {/* Debug info */}
            {owners.length > 0 && (
              <div style={{ 
                marginTop: '10px', 
                fontSize: '14px', 
                color: '#059669',
                fontWeight: '600',
                textAlign: 'center'
              }}>
                Ready to assign {owners.length} owner(s) to this file
              </div>
            )}
          </div>
          </div>
        </div>

        {/* Actions Section */}
        
      </div>
    </div>
  );

};

export default StatusModal;

